<template>
  <main>
    <header :class="style.returnAuthorizationBlock">
      <router-link to="registration" :class="style.button">
        <GhostButton size="M" :icon="closeIcon" />
      </router-link>
    </header>

    <ToastError ref="toastError" :text="toastErrorText" />

    <form novalidate @submit.prevent="verifyCode">
      <h1>Подтвердите email</h1>
      <p :class="style.description">
        Мы отправили 6-значный код на {{ email }}. Введите его, чтобы завершить
        регистрацию.
      </p>

      <div :class="style.mainForm">
        <div :class="style.inputBlock">
          <InputStyle
            placeholder="000000"
            description="Код из письма"
            size="M"
            type="text"
            errorText="Введите 6 цифр из письма"
            :isError="error"
            :disabled="isSubmitting"
            v-model="code"
            @input="onCodeInput"
          />
        </div>
      </div>

      <PrimaryButton
        text="Подтвердить"
        type="submit"
        :class="style.mainButton"
        :disabled="isSubmitting"
      />

      <SecondaryButton
        text="Отправить код повторно"
        :class="style.secondaryButton"
        :disabled="isSubmitting || isResending"
        @click="sendCode"
      />
    </form>
  </main>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {API} from "@/api/generated";
import style from "../../StartPage.module.scss";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import ToastError from "@/elements/UI/overlay/Toast/Error/ToastError.vue";
import closeIcon from "$images/icons/button/Name=Close, Size=16, Tone=Default.svg";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";

export default defineComponent({
  name: "RegistrationConfirmPage",

  components: {
    GhostButton,
    ToastError,
    InputStyle,
    PrimaryButton,
    SecondaryButton
  },

  data() {
    return {
      style,
      closeIcon,
      code: "",
      error: false,
      isSubmitting: false,
      isResending: false,
      toastErrorText: "Неверный или просроченный код"
    };
  },

  computed: {
    email(): string {
      const email = this.$route.query.email;

      return Array.isArray(email) ? email[0] || "" : email || "";
    },

    popupRef(): ToastType | null {
      return (
        (this.$refs.toastError as unknown as ToastType | undefined) ?? null
      );
    }
  },

  async created() {
    localStorage.clear();

    if (!this.email) {
      await this.$router.replace({name: "registration"});
      return;
    }

    await this.sendCode();
  },

  methods: {
    onCodeInput(event: Event): void {
      this.error = false;
      const target = event.target as HTMLInputElement | null;
      const value = target?.value || this.code;

      this.code = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 6);
    },

    showError(text = "Неверный или просроченный код"): void {
      this.error = true;
      this.toastErrorText = text;

      const popup = this.popupRef;

      if (popup && typeof popup.show === "function") {
        popup.show();
      }
    },

    async clearCaches(): Promise<void> {
      try {
        const keyList = await caches.keys();
        await Promise.all(keyList.map((key) => caches.delete(key)));
      } catch {}
    },

    async authorization(key: string): Promise<void> {
      try {
        const user = await API.getUser({
          headers: {
            Authorization: `Bearer ${key}`,
            Accept: "application/json"
          }
        });

        if (user?._id) {
          window.location.pathname = "/home";
        }
      } catch {
        this.showError("Не удалось войти в аккаунт");
      }
    },

    async sendCode(): Promise<void> {
      if (!this.email || this.isResending) {
        return;
      }

      this.isResending = true;

      try {
        await API.sendRegistrationCodeToEmail({
          body: {email: this.email}
        });
      } catch {
        this.showError("Не удалось отправить код на email");
      } finally {
        this.isResending = false;
      }
    },

    async verifyCode(): Promise<void> {
      if (this.isSubmitting) {
        return;
      }

      if (!/^\d{6}$/.test(this.code)) {
        this.showError("Введите 6 цифр из письма");
        return;
      }

      this.isSubmitting = true;

      try {
        const data = await API.verifyRegistrationCode({
          body: {email: this.email, code: this.code}
        });

        if (typeof data === "string" && data.length > 0) {
          await this.clearCaches();
          localStorage.setItem("keyUserAuth", data);
          await this.authorization(data);
        } else {
          this.showError();
        }
      } catch {
        this.showError();
      } finally {
        this.isSubmitting = false;
      }
    }
  },

  watch: {
    code(): void {
      this.error = false;
    }
  }
});
</script>
